import { useCallback, useMemo } from "react"
import {
  metricEntryFor,
  useMetricDictionary,
} from "~/ui-rtk/hooks/metric-dictionary"
import {
  type Query as TCubeQuery,
  type TimeDimensionGranularity,
} from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { getPivotData, resolveTimeDimensionKey } from "~/ui-rtk/utils/cube"
import {
  generalPostProcess,
  MetricsMapping,
  type PostProcessResultSync,
} from "~/ui-rtk/constants/metrics-mapping"
import useDate from "~/ui-rtk/hooks/date"
import {
  MetricDefinition,
  VISUALIZATIONS_MAP,
  AggregationType,
} from "~/ui-rtk/constants/charts"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import useBaselineRevenueMetric from "~/ui-rtk/hooks/baseline-revenue-metric"
import { useGaMultiPropertyEnabled } from "~/ui-rtk/hooks/ga-multi-property-access"
import {
  AMAZON_ADS_SPEND_SOURCE_KEY,
  AMAZON_REVENUE_SOURCE_KEY,
  SEARCH_CONSOLE_SOURCE_KEY,
  type AmazonFinancials,
  applyTypedColumnDeltas,
  type AvailabilityIndex,
  type AvailabilityPoint,
  availabilityWarningsForMetric,
  type BucketGranularity,
  buildAvailabilityIndex,
  buildTypedColumnDeltas,
  comparisonShiftForWindows,
  CALC_KEY_PREFIX,
  type CalculatedMetricDef,
  CUSTOM_KEY_PREFIX,
  customMetricCatalogEntries,
  type CustomMetricValueBucket,
  deriveBucketEnd,
  filterWholesaleMetrics,
  foldTargetsForMetric,
  getComputedMetric,
  initiativeTypeCatalogEntries,
  isAdditiveFormat,
  METRIC_AVAILABILITY_RULES,
  type InitiativeCategoryValue,
  type MetricCatalogEntry,
  resolveAmazonFinancials,
  resolveCatalogMetrics,
  resolveComparisonDate,
  resolveWeekStartDay,
  shiftComparisonStart,
  sourceForMetricKey,
  toFiniteNumber,
  TYPED_RATIO_RECOMPUTES,
  type TypedSeriesFold,
} from "~/shared/kpi"
import { useCompanyControllerGetFinancialsQuery } from "~/ui-rtk/api/companyApi"
import {
  type CompanyCustomMetricDto,
  useCompanyCustomMetricControllerListAllValuesQuery,
  useCompanyCustomMetricControllerListMetricsQuery,
} from "~/ui-rtk/api/companyCustomMetricApi"
import { useCustomAttributionControllerGetInitiativeTypesQuery } from "~/ui-rtk/api/customAttributionApi"
import {
  getInitiativeTypeCategory,
  InitiativeCategory,
} from "~/ui-rtk/constants/initiative-types"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useConnectedWholesaleServices } from "~/ui-rtk/hooks/wholesale-access"
import {
  getWindowComparisonKey,
  getYoyComparisonKey,
  type YoyGranularity,
  type YoyWindow,
} from "./yoy-comparison"
import {
  type ColumnBucket,
  type CustomMetricSeries,
  type KpiRow,
  type TypedRowInsertion,
  applyTypedRowInsertions,
  bucketCustomSeries,
  customCellEditability,
  processCustomMetrics,
} from "./utils/custom-metric-rows"
import {
  type CalcSecondaryRows,
  annotateAvailability,
  buildCalcSecondarySource,
  processCalculatedMetrics,
} from "./utils/calculated-metric-rows"
import {
  ADSET_INITIATIVE_MEASURES,
  ADSET_INITIATIVE_TYPE_DIMENSION,
  pivotInitiativeWindow,
} from "./utils/initiative-rows"
import {
  buildKpiWindows,
  weeklyBucketCount,
} from "./utils/kpi-compare-date-range"
import { mergeCompareWindows } from "./utils/merge-compare-windows"
import { partialBucketNote } from "./utils/partial-bucket-note"
import { MEDIA_CAMPAIGNS_QUERY_MEASURES } from "./constants/media-campaigns-query"

const GRANULARITY_SUFFIX: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "day",
  [AggregationType.WEEKLY]: "week",
  [AggregationType.MONTHLY]: "month",
}

const CUBE_GRANULARITY: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "day",
  [AggregationType.WEEKLY]: "week",
  [AggregationType.MONTHLY]: "month",
}

// The weekly result key may come back rewritten to the company's custom week
// granularity (see resolveTimeDimensionKey), so resolve it from the data.
function resolveDateKey(
  data: Array<Record<string, unknown>> | null | undefined,
  dateKeyPrefix: string,
  granularity: AggregationType,
): string {
  return resolveTimeDimensionKey(
    data,
    dateKeyPrefix,
    `${dateKeyPrefix}.${GRANULARITY_SUFFIX[granularity]}`,
  )
}

// Cube delivers numeric measures as strings; every row builder coerces via
// toFiniteNumber before arithmetic. Without this, processMetrics' YoY guard
// misfires on a zero comparison bucket (string "0" is truthy and `!== 0`),
// rendering "Infinity%" / "NaN%" in the YoY cells and the CSV export.
function processMetrics(
  data: Array<Record<string, unknown>> | null,
  dateKeyPrefix: string,
  granularity: AggregationType,
  metrics: MetricDefinition[],
  availability?: AvailabilityIndex,
  // Sibling datasets (brand value, media campaigns, ad-set initiatives) join
  // their rows to the MAIN dataset's column buckets by bucket start date
  // instead of by position: a sibling cube can lag the main cube by a day,
  // and on the first day of a reporting week its newest-N weekly trim then
  // starts one bucket earlier than the main dataset's, sliding every cell one
  // column to the right (MD-5705). `rowByBucketStart` is the sibling's
  // calcSecondaryRows index: merged rows by their own date, plus synthetic
  // compared.*-only rows for buckets the sibling's current window lacks, so
  // the YoY side survives the lag exactly as it does for calc metrics. Every
  // joined row carries the bucket's date, so a missing bucket renders 0 and
  // the availability warnings still resolve.
  columnJoin?: {
    columnBuckets: ColumnBucket[]
    rowByBucketStart: Map<string, Record<string, unknown>>
  },
) {
  if (!data || data.length === 0) return []

  const dateKey = resolveDateKey(data, dateKeyPrefix, granularity)
  let sortedData: Array<Record<string, unknown>>
  if (columnJoin) {
    sortedData = columnJoin.columnBuckets.map(bucket => ({
      [dateKey]: bucket.start,
      ...columnJoin.rowByBucketStart.get(bucket.start),
    }))
  } else {
    sortedData = [...data].sort(
      (a, b) =>
        new Date(a[dateKey] as string).getTime() -
        new Date(b[dateKey] as string).getTime(),
    )
  }

  return metrics.map(metricDef => {
    const row: Record<string, string | number | null | undefined> = {
      metric: metricDef.label,
      type: metricDef.type,
      key: metricDef.key,
      suffix: metricDef.suffix,
    }
    const hasAvailabilityRules =
      availability !== undefined &&
      (METRIC_AVAILABILITY_RULES[metricDef.key]?.length ?? 0) > 0

    sortedData.forEach((weekData, index) => {
      const weekKey = `week_${index + 1}`
      const currentValue = toFiniteNumber(weekData[metricDef.key])
      const previousValue = toFiniteNumber(
        weekData[`compared.${metricDef.key}`],
      )

      let periodChange: number | null = null
      if (previousValue !== 0) {
        periodChange = ((currentValue - previousValue) / previousValue) * 100
      } else if (currentValue === 0) {
        periodChange = 0
      }

      row[weekKey] = currentValue
      row[`${weekKey}_yoy_previous`] = previousValue
      row[`${weekKey}_yoy_change`] = periodChange

      if (hasAvailabilityRules && availability) {
        annotateAvailability(row, weekKey, weekData, dateKey, availability, d =>
          availabilityWarningsForMetric(availability, metricDef.key, d),
        )
      }
    })

    return row
  })
}

// Row builder for frontend-COMPUTED metrics (the `computed.` source). Mirrors
// `processMetrics` bucket-for-bucket so the output rows are shape-identical
// (week_N / week_N_yoy_previous / week_N_yoy_change), but instead of reading one
// cube column it evaluates the metric's registered compute fn over the whole
// row plus the company's Amazon cost settings. The compute fn reads source
// measures through a getter; for the YoY value we project the same getter onto
// the row's `compared.*` twins. Computed metrics read `metrics.*` columns, so
// `data` is the same merged `revenueKPIData` and the date key is `metrics.date`.
function processComputedMetrics(
  data: Array<Record<string, unknown>> | null,
  granularity: AggregationType,
  settings: AmazonFinancials,
  metrics: MetricDefinition[],
  availability?: AvailabilityIndex,
) {
  if (!data || data.length === 0 || metrics.length === 0) return []

  const dateKey = resolveDateKey(data, "metrics.date", granularity)
  const sortedData = [...data].sort(
    (a, b) =>
      new Date(a[dateKey] as string).getTime() -
      new Date(b[dateKey] as string).getTime(),
  )

  return metrics.map(metricDef => {
    const computed = getComputedMetric(metricDef.key)
    const row: Record<string, string | number | null | undefined> = {
      metric: metricDef.label,
      type: metricDef.type,
      key: metricDef.key,
      suffix: metricDef.suffix,
    }
    const hasAvailabilityRules =
      availability !== undefined &&
      (METRIC_AVAILABILITY_RULES[metricDef.key]?.length ?? 0) > 0

    sortedData.forEach((weekData, index) => {
      const weekKey = `week_${index + 1}`
      const currentValue = computed
        ? computed.compute(key => toFiniteNumber(weekData[key]), settings)
        : null
      const previousValue = computed
        ? computed.compute(
            key => toFiniteNumber(weekData[`compared.${key}`]),
            settings,
          )
        : null

      // Match processMetrics' YoY-change convention; null previous (e.g. a
      // margin with no comparison revenue) leaves the change blank.
      let periodChange: number | null = null
      if (previousValue !== null && previousValue !== 0) {
        periodChange =
          (((currentValue ?? 0) - previousValue) / previousValue) * 100
      } else if ((currentValue ?? 0) === 0 && (previousValue ?? 0) === 0) {
        periodChange = 0
      }

      row[weekKey] = currentValue
      row[`${weekKey}_yoy_previous`] = previousValue
      row[`${weekKey}_yoy_change`] = periodChange

      if (hasAvailabilityRules && availability) {
        annotateAvailability(row, weekKey, weekData, dateKey, availability, d =>
          availabilityWarningsForMetric(availability, metricDef.key, d),
        )
      }
    })

    return row
  })
}

/**
 * Derives one period bucket per column from the merged cube rows. The bucket
 * START is the cube's own date (date-only slice — no header round-trip, no
 * timezone drift), so weekly buckets already reflect the company's reporting
 * week start; the END is derived from the granularity. Index i here === the
 * `week_${i + 1}` column, because both iterate this same sorted revenueKPIData.
 */
function buildColumnBuckets(
  data: Array<Record<string, unknown>> | null,
  granularity: AggregationType,
): ColumnBucket[] {
  if (!data || data.length === 0) return []
  const dateKey = resolveDateKey(data, "metrics.date", granularity)
  const gran = GRANULARITY_SUFFIX[granularity] as BucketGranularity
  return [...data]
    .sort(
      (a, b) =>
        new Date(a[dateKey] as string).getTime() -
        new Date(b[dateKey] as string).getTime(),
    )
    .map((weekData, index) => {
      const start = String(weekData[dateKey] ?? "").slice(0, 10)
      return {
        key: `week_${index + 1}`,
        start,
        end: deriveBucketEnd(start, gran),
      }
    })
}

function queryTheCube(
  query: TCubeQuery,
  granularity: AggregationType,
  dateKeyPrefix: string,
  // Pairs each bucket with its comparison-window counterpart: calendar-YoY
  // keys (getYoyComparisonKey) on the default dashboard path, offset-from-
  // window-start keys (getWindowComparisonKey) for explicit compare windows.
  // Must be referentially stable (useCallback) — it keys the merge memo.
  comparisonKeyOf: (dateStr: string, window: YoyWindow) => string,
  // Weekly merged datasets keep this many most-recent buckets (range-derived,
  // see weeklyBucketCount; the default window yields 52). Ignored at other
  // granularities.
  weeklyKeepCount: number,
  // Optional per-window transform applied to the raw compareDateRange rows
  // BEFORE the YoY merge. The merge (and processMetrics) requires exactly one
  // row per date bucket, so dimension-grouped queries must collapse their
  // per-dimension rows into one wide row per date here (see
  // pivotInitiativeWindow). Must be referentially stable (useCallback).
  pivotWindow?: (
    rows: Array<Record<string, unknown>>,
    dateKey: string,
  ) => Array<Record<string, unknown>>,
) {
  const { resultSet, isLoading } = useCubeQuery(query)

  // `merged` is the one-row-per-current-bucket dataset with compared.* YoY
  // twins (what every row builder consumes); `untrimmed` is the same rows
  // before the weekly trim; `comparison` is the raw (pivoted, sorted)
  // comparison-window rows — the merge drops comparison buckets with no
  // current partner, so calc-operand joins need them separately (see
  // buildCalcSecondarySource).
  const processed = useMemo((): {
    merged: Array<Record<string, unknown>> | null
    untrimmed: Array<Record<string, unknown>> | null
    comparison: Array<Record<string, unknown>> | null
  } => {
    if (!resultSet) return { merged: null, untrimmed: null, comparison: null }

    // For compareDateRange queries, access the underlying loadResponses
    const resultSetWithResponses = resultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }
    if (
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 0
    ) {
      const rawCurrentData = resultSetWithResponses.loadResponses[1]?.data || []
      const rawComparisonData =
        resultSetWithResponses.loadResponses[0]?.data || []
      const dateKey = resolveDateKey(
        rawCurrentData.length > 0 ? rawCurrentData : rawComparisonData,
        dateKeyPrefix,
        granularity,
      )
      const currentData = pivotWindow
        ? pivotWindow(rawCurrentData, dateKey)
        : rawCurrentData
      const comparisonData = pivotWindow
        ? pivotWindow(rawComparisonData, dateKey)
        : rawComparisonData
      const sortByDate = (
        a: Record<string, unknown>,
        b: Record<string, unknown>,
      ) =>
        new Date(a[dateKey] as string).getTime() -
        new Date(b[dateKey] as string).getTime()
      const sortedCurrentData = [...currentData].sort(sortByDate)
      const sortedComparisonData = [...comparisonData].sort(sortByDate)

      // Bucket pairing lives in mergeCompareWindows (extracted verbatim so it
      // is unit-testable): calendar-based YoY keys by default (same month/day,
      // 364-day week pairing, month of year — a true year-over-year even when
      // the comparison window is shorter), or offset-from-window-start keys
      // for explicit compare windows. See getYoyComparisonKey /
      // getWindowComparisonKey.
      const merged = mergeCompareWindows(
        sortedCurrentData,
        sortedComparisonData,
        dateKey,
        comparisonKeyOf,
      )
      // The default weekly windows span 365 days and are not aligned to the
      // company week start, so they always cover 53 week buckets, the oldest
      // usually partial. Week-of-year keying used to hide the 53rd bucket
      // through an accidental key collision; keep the most recent
      // `weeklyKeepCount` explicitly instead (range-derived — 52 for the
      // default window, N for an N-week explicit range), so every weekly
      // dataset trims the same bucket when the cubes are equally fresh. The
      // sibling datasets then join to the main dataset's columns by bucket
      // start date (processMetrics with columnJoin), so a lagging sibling
      // cannot shift its cells. That join reads `untrimmed`: when the MAIN
      // cube is the one lagging, the main dataset keeps an oldest week that a
      // sibling's own trim drops, and the main columns already decide which
      // buckets show. The raw comparison rows stay untrimmed — they are
      // consumed via keyed lookups (buildCalcSecondarySource).
      return {
        merged:
          granularity === AggregationType.WEEKLY
            ? merged.slice(-weeklyKeepCount)
            : merged,
        untrimmed: merged,
        comparison: sortedComparisonData,
      }
    }

    // Fallback to regular processing
    const columns = resultSet.tableColumns()
    const pivotData = getPivotData(resultSet, query)
    const fallbackRows = generalPostProcess(
      pivotData as Array<Record<string, string | number>>,
      columns as unknown as Array<{
        key: string
        dataIndex?: string
        type?: string
        [key: string]: string | undefined
      }>,
    )
    return {
      merged: fallbackRows,
      untrimmed: fallbackRows,
      comparison: null,
    }
  }, [
    resultSet,
    granularity,
    dateKeyPrefix,
    comparisonKeyOf,
    weeklyKeepCount,
    pivotWindow,
  ])

  return {
    isLoading,
    dataSource: processed.merged,
    untrimmedDataSource: processed.untrimmed,
    comparisonDataSource: processed.comparison,
  }
}

/**
 * Optional window parameters for the KPI data pipeline (the upcoming
 * Metrics-Explorer view; the dashboard passes nothing and keeps the fixed
 * trailing windows byte-for-byte).
 */
export interface KpiConnectParams {
  /**
   * Explicit current window [start, end] (inclusive; read as LOCAL calendar
   * dates and serialized without UTC conversion, matching the dashboard's
   * window builders). Omitted → the fixed trailing window per granularity.
   */
  dateRange?: [Date, Date]
  /**
   * Comparison window. 'yoy' (default) applies the dashboard's year-back
   * construction to the current window (calendar-identity bucket pairing,
   * unchanged). An explicit [start, end] compares against that exact window,
   * with buckets paired by offset from each window's start (day index /
   * snapped 7-day index / calendar-month index) and custom/typed metric
   * comparisons shifted by the same window offset.
   */
  compareRange?: [Date, Date] | "yoy"
}

export default function useConnect(
  granularity: AggregationType = AggregationType.WEEKLY,
  isShopifyConnected = true,
  params?: KpiConnectParams,
) {
  const dayjs = useDate()
  const { depVar, amazonEnabled, depVarScaler, toggleAmazon } = useDepVar()
  const baselineRevenueMetric = useBaselineRevenueMetric()
  const isGaMultiPropertyEnabled = useGaMultiPropertyEnabled()
  const connectedWholesaleServices = useConnectedWholesaleServices()
  const isWholesaleConnected = connectedWholesaleServices.length > 0

  // Metric display names come from the shared Metric Dictionary (single source
  // of truth); the catalog's own label is the fallback for anything not
  // catalogued (calc/custom metrics). Applied to every metric list before it
  // enters the row builders. Idempotent, so overlapping applications are safe.
  const { metricMap } = useMetricDictionary()
  const withDictLabels = useCallback(
    <T extends { key: string; label: string }>(metrics: T[]): T[] =>
      metrics.map(metric => {
        const title = metricEntryFor(metricMap, metric.key)?.title
        return title && title !== metric.label
          ? { ...metric, label: title }
          : metric
      }),
    [metricMap],
  )

  // Financial Settings cost assumptions feed the frontend-computed Amazon
  // Contribution metrics (the only non-cube input in this hook). Single current
  // value per company (no time dimension), so it applies uniformly to every
  // period; defaults fill any unset field.
  const currentCompany = useCurrentCompany()
  const { data: financials, isFetching: financialsLoading } =
    useCompanyControllerGetFinancialsQuery(
      { id: currentCompany?.id ?? "" },
      { skip: !currentCompany?.id },
    )
  const amazonFinancials = useMemo(
    () => resolveAmazonFinancials(financials),
    [financials],
  )
  // Only used for the YoY week-pairing math; result-row date keys are resolved
  // from the response itself (resolveDateKey), so they never depend on this
  // value being in sync with the cube token's claim.
  const weekStartDay = useMemo(
    () => resolveWeekStartDay(financials),
    [financials],
  )

  // THE window computation: every pipeline query (main KPI, brand value,
  // media campaigns, ad-set initiatives, daily availability) reads this one
  // object, so the five fetch windows can never drift apart within a render
  // (the column buckets come from the main dataset alone). Date params are
  // reduced to epoch-ms primitives so a caller passing fresh (but equal)
  // Date/tuple instances each render cannot churn the query identities.
  const dateRangeParam = params?.dateRange
  const compareRangeParam = params?.compareRange
  const rangeStartMs = dateRangeParam ? dateRangeParam[0].getTime() : null
  const rangeEndMs = dateRangeParam ? dateRangeParam[1].getTime() : null
  const compareStartMs = Array.isArray(compareRangeParam)
    ? compareRangeParam[0].getTime()
    : null
  const compareEndMs = Array.isArray(compareRangeParam)
    ? compareRangeParam[1].getTime()
    : null
  const kpiWindows = useMemo(
    () =>
      buildKpiWindows(granularity, {
        range:
          rangeStartMs !== null && rangeEndMs !== null
            ? [new Date(rangeStartMs), new Date(rangeEndMs)]
            : undefined,
        // undefined ≡ 'yoy' (the builder's default), so the literal never
        // needs threading here.
        compare:
          compareStartMs !== null && compareEndMs !== null
            ? [new Date(compareStartMs), new Date(compareEndMs)]
            : undefined,
      }),
    [granularity, rangeStartMs, rangeEndMs, compareStartMs, compareEndMs],
  )

  // Range-derived weekly trim (52 for the default window, N for an N-week
  // explicit range); a plain number, so it is a stable memo dep by value.
  const weeklyKeepCount = weeklyBucketCount(kpiWindows.currentRange)

  // Bucket-pairing key shared by every merge (and the calc secondary-source
  // synthesis): calendar-YoY keys on the default path — UNCHANGED, including
  // the monthly 13→12 comparison-bucket collapse — and offset-from-window-
  // start keys for an explicit compare window (an arbitrary window is not a
  // whole-year shift, so calendar keys would match nothing and blank the
  // comparisons).
  const comparisonKeyOf = useCallback(
    (dateStr: string, window: YoyWindow) => {
      const cubeGranularity = CUBE_GRANULARITY[granularity] as YoyGranularity
      if (kpiWindows.compareMode === "window") {
        return getWindowComparisonKey(
          dateStr,
          cubeGranularity,
          weekStartDay,
          window,
          {
            currentStart: kpiWindows.currentRange[0],
            comparisonStart: kpiWindows.comparisonRange[0],
          },
        )
      }
      return getYoyComparisonKey(dateStr, cubeGranularity, weekStartDay, window)
    },
    [granularity, weekStartDay, kpiWindows],
  )

  // Comparison-bucket shift for the custom/typed metric series and the
  // availability index: undefined on the default path (the year-back
  // shiftComparisonStart rule, untouched), the window-offset shift for an
  // explicit compare window — otherwise custom and typed metrics would
  // silently compare against a different period than the cube metrics.
  const comparisonStartOf = useMemo(
    () =>
      kpiWindows.compareMode === "window"
        ? comparisonShiftForWindows(
            GRANULARITY_SUFFIX[granularity] as BucketGranularity,
            kpiWindows.currentRange[0],
            kpiWindows.comparisonRange[0],
            weekStartDay,
          )
        : undefined,
    [kpiWindows, granularity, weekStartDay],
  )

  // User-defined custom metrics (definitions + all values for the company).
  // Values are manually entered, so the volume is small — one unfiltered fetch
  // covers every visible bucket and its prior-period comparison.
  // isLoading (first fetch only), NOT isFetching: every value save (inline
  // edit or manager modal) invalidates these queries, and gating on refetches
  // would swap the tables to skeletons and re-fire the auto-scroll on each
  // save. Stale rows simply update in place when the refetch lands.
  const { data: customMetricsList, isLoading: customMetricsFirstLoad } =
    useCompanyCustomMetricControllerListMetricsQuery()
  const { data: customValues, isLoading: customValuesFirstLoad } =
    useCompanyCustomMetricControllerListAllValuesQuery({})
  const customMetricsLoading = customMetricsFirstLoad || customValuesFirstLoad

  const customMetrics: CompanyCustomMetricDto[] = useMemo(
    () => customMetricsList ?? [],
    [customMetricsList],
  )

  // Synthesized catalog entries (custom.<id> → name/format) threaded into
  // resolveCatalogMetrics / the pickers; never merged into the frozen catalog.
  const customEntries: MetricCatalogEntry[] = useMemo(
    () =>
      customMetricCatalogEntries(
        customMetrics.map(({ id, name, format }) => ({ id, name, format })),
      ),
    [customMetrics],
  )

  // The company's initiative types (predefined + custom). Custom types drive
  // two things for the `initiatives` source: synthesized catalog entries for
  // their per-type breakout keys, and their persisted Demand Gen / Demand
  // Capture category for the dynamic category rollups.
  const { data: initiativeTypeOptions, isFetching: initiativeTypesFetching } =
    useCustomAttributionControllerGetInitiativeTypesQuery()
  const customInitiativeTypes = useMemo(
    () => (initiativeTypeOptions ?? []).filter(option => option.isCustom),
    [initiativeTypeOptions],
  )

  // Synthesized catalog entries for CUSTOM_* initiative types (predefined
  // types have static catalog entries via the initiativeTypeMetrics group).
  const initiativeEntries: MetricCatalogEntry[] = useMemo(
    () =>
      initiativeTypeCatalogEntries(
        customInitiativeTypes.map(({ value, label }) => ({ value, label })),
      ),
    [customInitiativeTypes],
  )

  // Every per-render synthesized entry, for the pickers and key resolution
  // (the calc-metric builder receives the same lists as separate props).
  const catalogExtraEntries = useMemo(
    () => [...customEntries, ...initiativeEntries],
    [customEntries, initiativeEntries],
  )

  // Category resolver for the DG/DC rollups: predefined types use the static
  // map, custom types their persisted category, unknown values default to
  // Demand Capture (same convention as the Budget Recommender).
  const categoryForInitiativeType = useCallback(
    (typeValue: string): InitiativeCategoryValue =>
      getInitiativeTypeCategory(typeValue, customInitiativeTypes) ===
      InitiativeCategory.DEMAND_GENERATION
        ? "DEMAND_GENERATION"
        : "DEMAND_CAPTURE",
    [customInitiativeTypes],
  )

  const pivotAdsetInitiativeWindow = useCallback(
    (rows: Array<Record<string, unknown>>, dateKey: string) =>
      pivotInitiativeWindow(
        rows,
        dateKey,
        "company_media_metrics_adsets.md_date",
        categoryForInitiativeType,
      ),
    [categoryForInitiativeType],
  )

  const valuesByMetricId = useMemo(() => {
    const map = new Map<string, CustomMetricValueBucket[]>()
    for (const value of customValues ?? []) {
      const list = map.get(value.metricId) ?? []
      list.push({
        startDate: value.startDate,
        endDate: value.endDate,
        amount: value.amount,
      })
      map.set(value.metricId, list)
    }
    return map
  }, [customValues])

  const revenueKPIQuery = useMemo(() => {
    const query = MetricsMapping.revenue_kpi_table.q
    const cubeGranularity = CUBE_GRANULARITY[
      granularity
    ] as TimeDimensionGranularity
    const baseQuery =
      typeof query === "function"
        ? query({
            granularity: cubeGranularity,
            depVarScaler,
            // Always the shared window object — the default windows are
            // byte-identical to the builder's own (spec-pinned), and explicit
            // Explorer ranges flow through the same query builder.
            compareDateRange: [
              kpiWindows.comparisonRange,
              kpiWindows.currentRange,
            ],
          })
        : query

    // Handle array case
    if (Array.isArray(baseQuery)) {
      return baseQuery.map(q => ({
        ...q,
        timeDimensions:
          q.timeDimensions?.map(td => ({
            ...td,
            granularity: CUBE_GRANULARITY[granularity],
          })) || [],
      }))
    }

    // Update time dimension based on granularity while preserving compareDateRange
    const baseQueryWithTimeDimensions = baseQuery as {
      timeDimensions?: Array<Record<string, unknown>>
    }
    const originalTimeDimensions =
      baseQueryWithTimeDimensions.timeDimensions || []
    return {
      ...baseQuery,
      timeDimensions: originalTimeDimensions.map(
        (td: Record<string, unknown>) => ({
          ...td,
          granularity: CUBE_GRANULARITY[granularity],
        }),
      ),
    }
  }, [granularity, depVarScaler, kpiWindows])

  const brandValueQuery = useMemo(() => {
    const [comparisonStartStr, comparisonEndStr] = kpiWindows.comparisonRange
    const [currentStartStr, currentEndStr] = kpiWindows.currentRange

    return {
      measures: [
        "company_platform_brand_value.total_brand_value",
        "company_platform_brand_value.ad_channel_group_brand_value",
        "company_platform_brand_value.organic_channel_group_brand_value",
        "company_platform_brand_value.mentions_channel_group_brand_value",
        "company_platform_brand_value.subscription_channel_group_brand_value",
        "company_platform_brand_value.search_channel_group_brand_value",
        "company_platform_brand_value.unattributed_channel_group_brand_value",
      ],
      ...(depVarScaler.length > 0
        ? {
            filters: [
              {
                and: [
                  {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  },
                ],
              },
            ],
          }
        : {}),
      timeDimensions: [
        {
          dimension: "company_platform_brand_value.engagement_date",
          granularity: CUBE_GRANULARITY[granularity],
          compareDateRange: [
            [comparisonStartStr, comparisonEndStr],
            [currentStartStr, currentEndStr],
          ],
        },
      ],
    }
  }, [granularity, depVarScaler, kpiWindows])

  // Modeled paid-media metrics come from the campaign media cube
  // (`company_media_metrics_campaigns`), a different cube than the main KPI
  // `metrics` query. We fetch them with their own query — grouped only by the
  // date dimension so the cube returns one daily/weekly/monthly row summed
  // across all campaigns — and the dep_var_scaler filter mirrors the brand
  // value query so we don't sum across model variants. These rows are spliced
  // into custom-report groups by `buildCustomGroupData` (catalog-only; no
  // default dashboard table renders them).
  const mediaCampaignsQuery = useMemo(() => {
    const [comparisonStartStr, comparisonEndStr] = kpiWindows.comparisonRange
    const [currentStartStr, currentEndStr] = kpiWindows.currentRange

    return {
      // Every catalog key with source `media_campaigns` must appear here —
      // a key missing from this list silently renders 0 for all dates (the
      // "media campaigns catalog coverage" spec in metrics-mapping.test.ts
      // enforces the sync via MEDIA_CAMPAIGNS_QUERY_MEASURES).
      measures: MEDIA_CAMPAIGNS_QUERY_MEASURES,
      ...(depVarScaler.length > 0
        ? {
            filters: [
              {
                and: [
                  {
                    dimension: "company_media_metrics_campaigns.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  },
                ],
              },
            ],
          }
        : {}),
      timeDimensions: [
        {
          dimension: "company_media_metrics_campaigns.md_date",
          granularity: CUBE_GRANULARITY[granularity],
          compareDateRange: [
            [comparisonStartStr, comparisonEndStr],
            [currentStartStr, currentEndStr],
          ],
        },
      ],
    }
  }, [granularity, depVarScaler, kpiWindows])

  // Initiative-type breakouts (the `initiatives` source) come from the ADSET
  // media cube — the only cube carrying initiative_type — grouped by the
  // initiative_type dimension. That yields multiple rows per date bucket, so
  // pivotAdsetInitiativeWindow collapses each compareDateRange window into
  // one wide row per date (initiative.* / initiative_category.* keys) before
  // the YoY merge. dep_var_scaler mirrors the other secondary queries. The
  // explicit limit keeps daily granularity × many initiative types safely
  // under Cube's per-request row cap (default is 10k).
  const adsetInitiativeQuery = useMemo(() => {
    const [comparisonStartStr, comparisonEndStr] = kpiWindows.comparisonRange
    const [currentStartStr, currentEndStr] = kpiWindows.currentRange

    return {
      measures: ADSET_INITIATIVE_MEASURES,
      dimensions: [ADSET_INITIATIVE_TYPE_DIMENSION],
      limit: 50000,
      ...(depVarScaler.length > 0
        ? {
            filters: [
              {
                and: [
                  {
                    dimension: "company_media_metrics_adsets.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  },
                ],
              },
            ],
          }
        : {}),
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
          granularity: CUBE_GRANULARITY[granularity],
          compareDateRange: [
            [comparisonStartStr, comparisonEndStr],
            [currentStartStr, currentEndStr],
          ],
        },
      ],
    }
  }, [granularity, depVarScaler, kpiWindows])

  const { dataSource: revenueKPIRawData, isLoading } = queryTheCube(
    revenueKPIQuery as TCubeQuery,
    granularity,
    "metrics.date",
    comparisonKeyOf,
    weeklyKeepCount,
  )

  // Apply revenue-specific post-processor (Facebook Ads granularity selection).
  // "Base" = pipeline data only: column buckets and the data-availability
  // indexes read this, while the row builders read the typed-custom-metric
  // FOLDED dataset derived from it below (so availability warnings keep
  // describing what the pipeline actually delivered).
  const revenueKPIBaseData = useMemo(() => {
    if (!revenueKPIRawData) return null
    const postProcessor = MetricsMapping.revenue_kpi_table.postProcess
    if (!postProcessor) return revenueKPIRawData
    return (
      postProcessor(revenueKPIRawData, {
        granularity: GRANULARITY_SUFFIX[granularity],
      }) as PostProcessResultSync
    )[0]
  }, [revenueKPIRawData, granularity])

  const {
    dataSource: brandValueBreakdownRawData,
    untrimmedDataSource: brandValueUntrimmedData,
    comparisonDataSource: brandValueComparisonData,
    isLoading: brandValueIsLoading,
  } = queryTheCube(
    brandValueQuery as TCubeQuery,
    granularity,
    "company_platform_brand_value.engagement_date",
    comparisonKeyOf,
    weeklyKeepCount,
  )

  const {
    dataSource: mediaCampaignsRawData,
    untrimmedDataSource: mediaCampaignsUntrimmedData,
    comparisonDataSource: mediaCampaignsComparisonData,
    isLoading: mediaCampaignsIsLoading,
  } = queryTheCube(
    mediaCampaignsQuery as TCubeQuery,
    granularity,
    "company_media_metrics_campaigns.md_date",
    comparisonKeyOf,
    weeklyKeepCount,
  )

  const {
    dataSource: adsetInitiativeData,
    untrimmedDataSource: adsetInitiativeUntrimmedData,
    comparisonDataSource: adsetInitiativeComparisonData,
    isLoading: adsetInitiativeIsLoading,
  } = queryTheCube(
    adsetInitiativeQuery as TCubeQuery,
    granularity,
    "company_media_metrics_adsets.md_date",
    comparisonKeyOf,
    weeklyKeepCount,
    pivotAdsetInitiativeWindow,
  )

  // Custom-type categories (and labels) come from the initiative-types fetch,
  // so the `initiatives` source stays "loading" until both the cube rows and
  // the type metadata have resolved — otherwise custom types would briefly
  // bucket into Demand Capture and re-shuffle when the fetch lands.
  const initiativesIsLoading =
    adsetInitiativeIsLoading || initiativeTypesFetching

  // Day-granularity evidence for the data-availability warnings. Week/month
  // rows can only bound a source to whole buckets — a source starting or
  // ending mid-week looks fully available from its boundary bucket's start —
  // so the "missing for ANY day in the period" check needs daily bounds. This
  // fetches just the registry's limiting source measures across the full
  // fetched horizon (kpiWindows.availabilityRange — the union span of both
  // windows, [comparison start, current end] on the default path);
  // calculated-metric leaves and custom series intentionally stay
  // bucket-resolution (see data-availability.ts). Warnings sharpen when these
  // rows land; until then the index falls back to bucket-resolution bounds.
  // The query below and the availability indexes' lower-edge clamp
  // (firstFetchedDate) both read kpiWindows.availabilityRange, so they agree
  // on the exact fetched window.
  const availabilityDailyQuery = useMemo(() => {
    const [availabilityStartStr, availabilityEndStr] =
      kpiWindows.availabilityRange
    return {
      measures: [
        AMAZON_REVENUE_SOURCE_KEY,
        AMAZON_ADS_SPEND_SOURCE_KEY,
        SEARCH_CONSOLE_SOURCE_KEY,
      ],
      ...(depVarScaler.length > 0
        ? {
            filters: [
              {
                and: [
                  {
                    dimension: "metrics.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  },
                ],
              },
            ],
          }
        : {}),
      timeDimensions: [
        {
          dimension: "metrics.date",
          granularity: "day",
          dateRange: [availabilityStartStr, availabilityEndStr],
        },
      ],
    }
  }, [kpiWindows, depVarScaler])

  const { resultSet: availabilityDailyResultSet } = useCubeQuery(
    availabilityDailyQuery as TCubeQuery,
  )

  // Plain (non-compare) query: a single loadResponse holds the raw day rows.
  const availabilityDailyRows = useMemo(() => {
    const withResponses = availabilityDailyResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    } | null
    return withResponses?.loadResponses?.[0]?.data ?? null
  }, [availabilityDailyResultSet])

  // Period buckets (start/end per column) derived from the cube's own dates,
  // and each custom metric's values bucketed onto them (+ prior period). Both
  // are keyed to the same sorted revenueKPIData that drives columnConfig, so
  // index i lines up with the `week_${i + 1}` columns everywhere.
  const columnBuckets = useMemo(
    () => buildColumnBuckets(revenueKPIBaseData, granularity),
    [revenueKPIBaseData, granularity],
  )

  const customSeriesById = useMemo(() => {
    const gran = GRANULARITY_SUFFIX[granularity] as BucketGranularity
    const map = new Map<string, CustomMetricSeries>()
    for (const metric of customMetrics) {
      map.set(
        metric.id,
        bucketCustomSeries(
          valuesByMetricId.get(metric.id) ?? [],
          columnBuckets,
          gran,
          isAdditiveFormat(metric.format),
          // Explicit-compare-window shift; undefined on the default YoY path.
          comparisonStartOf,
        ),
      )
    }
    return map
  }, [
    customMetrics,
    valuesByMetricId,
    columnBuckets,
    granularity,
    comparisonStartOf,
  ])

  // TYPED custom metrics (type + subtype on the metric): their bucketed series
  // fold into the aggregate measure columns BEFORE the row builders run — see
  // ~/shared/kpi/typed-custom-metrics.ts for targets and rationale. The main
  // dataset also re-derives the affected cube ratio cells (MER family, aMER,
  // CM%); the initiative dataset receives the DG/DC spend deltas.
  const typedSeriesFolds = useMemo(() => {
    const main: TypedSeriesFold[] = []
    const initiative: TypedSeriesFold[] = []
    for (const metric of customMetrics) {
      const targets = foldTargetsForMetric(metric)
      if (!targets) continue
      const series = customSeriesById.get(metric.id)
      if (!series) continue
      if (targets.mainColumns.length > 0) {
        main.push({ series, columns: targets.mainColumns, sign: targets.sign })
      }
      if (targets.initiativeColumns.length > 0) {
        initiative.push({
          series,
          columns: targets.initiativeColumns,
          sign: targets.sign,
        })
      }
    }
    return { main, initiative }
  }, [customMetrics, customSeriesById])

  const typedMainDeltas = useMemo(
    () => buildTypedColumnDeltas(typedSeriesFolds.main, columnBuckets.length),
    [typedSeriesFolds, columnBuckets],
  )
  const typedInitiativeDeltas = useMemo(
    () =>
      buildTypedColumnDeltas(typedSeriesFolds.initiative, columnBuckets.length),
    [typedSeriesFolds, columnBuckets],
  )

  // The folded datasets every row builder consumes. Identity-stable when no
  // typed custom metrics have values in the window.
  const revenueKPIData = useMemo(
    () =>
      applyTypedColumnDeltas({
        rows: revenueKPIBaseData,
        dateKey: resolveDateKey(
          revenueKPIBaseData,
          "metrics.date",
          granularity,
        ),
        buckets: columnBuckets,
        deltas: typedMainDeltas,
        ratioRecomputes: TYPED_RATIO_RECOMPUTES,
      }),
    [revenueKPIBaseData, granularity, columnBuckets, typedMainDeltas],
  )

  // Untrimmed: these rows only reach the tables through the column join
  // (calcSecondaryRows.initiatives), and a row outside the main columns is
  // left as it is by the fold.
  const foldedAdsetInitiativeData = useMemo(
    () =>
      applyTypedColumnDeltas({
        rows: adsetInitiativeUntrimmedData,
        dateKey: resolveDateKey(
          adsetInitiativeUntrimmedData,
          "company_media_metrics_adsets.md_date",
          granularity,
        ),
        buckets: columnBuckets,
        deltas: typedInitiativeDeltas,
        // The adset dataset is not bucket-complete (paused campaigns, no
        // platform ads at all) — DG/DC-tagged custom spend still needs a row
        // for those buckets or it would vanish from the category rollups.
        synthesizeMissingBucketRows: true,
      }),
    [
      adsetInitiativeUntrimmedData,
      granularity,
      columnBuckets,
      typedInitiativeDeltas,
    ],
  )

  // Inline-edit metadata for KPITable: each column's exact [start, end] bucket
  // (an edited cell upserts that range verbatim — never re-derived from the
  // formatted header or snapped client-side, so it always matches the rendered
  // column) plus the per-cell read-only gate for complete finer roll-ups.
  const customEditMeta = useMemo(() => {
    const editableCellsByMetricId = new Map<string, boolean[]>()
    for (const metric of customMetrics) {
      editableCellsByMetricId.set(
        metric.id,
        customCellEditability(
          valuesByMetricId.get(metric.id) ?? [],
          columnBuckets,
          isAdditiveFormat(metric.format),
        ),
      )
    }
    return { columnBuckets, editableCellsByMetricId }
  }, [customMetrics, valuesByMetricId, columnBuckets])

  // Sibling-dataset rows joined by bucket date, for calculated-metric operands
  // whose values live outside the merged revenueKPIData rows. Buckets a
  // sibling has no current-window row for get synthetic compared.*-only rows
  // from its comparison window (paired by the merge's own YoY calendar key),
  // so a formula's YoY side survives sibling lag (see buildCalcSecondarySource).
  const calcSecondaryRows = useMemo<CalcSecondaryRows>(() => {
    // Same pairing key the merges use (calendar-YoY by default, offset keys
    // for explicit compare windows), so the synthetic compared.*-only rows
    // pair identically to a real merge.
    const yoyKeyOf = comparisonKeyOf
    const bucketStarts = columnBuckets.map(bucket => bucket.start)
    const build = (
      mergedRows: Array<Record<string, unknown>> | null,
      comparisonRows: Array<Record<string, unknown>> | null,
      dateKeyPrefix: string,
    ) =>
      buildCalcSecondarySource({
        mergedRows,
        comparisonRows,
        dateKey: resolveDateKey(
          mergedRows ?? comparisonRows,
          dateKeyPrefix,
          granularity,
        ),
        bucketStarts,
        yoyKeyOf,
      })
    // Untrimmed merged rows (see queryTheCube): the main columns pick the buckets.
    return {
      brand_value: build(
        brandValueUntrimmedData,
        brandValueComparisonData,
        "company_platform_brand_value.engagement_date",
      ),
      media_campaigns: build(
        mediaCampaignsUntrimmedData,
        mediaCampaignsComparisonData,
        "company_media_metrics_campaigns.md_date",
      ),
      // Folded so calc formulas over DG/DC spend agree with the displayed
      // rows. (The raw comparison rows stay unfolded — they only backfill
      // compared.* cells for buckets the current window lacks entirely.)
      initiatives: build(
        foldedAdsetInitiativeData,
        adsetInitiativeComparisonData,
        "company_media_metrics_adsets.md_date",
      ),
    }
  }, [
    brandValueUntrimmedData,
    brandValueComparisonData,
    mediaCampaignsUntrimmedData,
    mediaCampaignsComparisonData,
    foldedAdsetInitiativeData,
    adsetInitiativeComparisonData,
    columnBuckets,
    granularity,
    comparisonKeyOf,
  ])

  // Data-availability index: each limiting source's first/last nonzero date,
  // evidenced by the already-loaded KPI rows (current + comparison windows).
  // Row builders use it to attach per-bucket warnings for composite metrics
  // whose components (Amazon revenue/ad spend, Search Console) have shorter
  // histories. This base index reads only the MAIN cube's evidence (bucket
  // rows + the day-resolution availability rows) — it feeds every builtin
  // table, so its identity must not churn when the SIBLING queries land.
  const availabilityIndex = useMemo(
    () =>
      buildAvailabilityIndex({
        // BASE rows: availability describes pipeline data — a typed custom
        // value folded into e.g. amazon_total_revenue must not fake evidence
        // that the Amazon connector delivered anything.
        rows: revenueKPIBaseData,
        dateKey: resolveDateKey(
          revenueKPIBaseData,
          "metrics.date",
          granularity,
        ),
        granularity: GRANULARITY_SUFFIX[granularity] as BucketGranularity,
        amazonEnabled,
        dailyRows: availabilityDailyRows,
        dailyDateKey: resolveDateKey(
          availabilityDailyRows,
          "metrics.date",
          AggregationType.DAILY,
        ),
        dailyWindowStart: kpiWindows.availabilityRange[0],
        comparisonStartOf,
      }),
    [
      revenueKPIBaseData,
      granularity,
      amazonEnabled,
      availabilityDailyRows,
      kpiWindows,
      comparisonStartOf,
    ],
  )

  // Extended index for CUSTOM GROUPS only: the sibling cube datasets are
  // indexed too (so calculated metrics can bound brand-value/media-campaigns/
  // initiative leaves) and custom-metric series are registered (so
  // `custom.<id>` leaves bound as well). Values are identical to the base
  // index for metrics.* sources — kept separate purely so sibling-query
  // arrivals don't recompute every builtin table's rows.
  const customGroupAvailabilityIndex = useMemo(() => {
    const gran = GRANULARITY_SUFFIX[granularity] as BucketGranularity
    const index = buildAvailabilityIndex({
      // BASE rows here too — see availabilityIndex.
      rows: revenueKPIBaseData,
      dateKey: resolveDateKey(revenueKPIBaseData, "metrics.date", granularity),
      granularity: gran,
      amazonEnabled,
      extraRowSets: [
        {
          rows: brandValueBreakdownRawData,
          dateKey: resolveDateKey(
            brandValueBreakdownRawData,
            "company_platform_brand_value.engagement_date",
            granularity,
          ),
        },
        {
          rows: mediaCampaignsRawData,
          dateKey: resolveDateKey(
            mediaCampaignsRawData,
            "company_media_metrics_campaigns.md_date",
            granularity,
          ),
        },
        {
          rows: adsetInitiativeData,
          dateKey: resolveDateKey(
            adsetInitiativeData,
            "company_media_metrics_adsets.md_date",
            granularity,
          ),
        },
      ],
      dailyRows: availabilityDailyRows,
      dailyDateKey: resolveDateKey(
        availabilityDailyRows,
        "metrics.date",
        AggregationType.DAILY,
      ),
      dailyWindowStart: kpiWindows.availabilityRange[0],
      comparisonStartOf,
    })
    for (const metric of customMetrics) {
      const series = customSeriesById.get(metric.id)
      if (!series) continue
      const points: AvailabilityPoint[] = []
      columnBuckets.forEach((bucket, i) => {
        points.push({ date: bucket.start, value: series.current[i] })
        points.push({
          // Same shift the series' previous side was bucketed with — the
          // window offset for explicit compare windows, year-back otherwise.
          date: comparisonStartOf
            ? comparisonStartOf(bucket.start)
            : shiftComparisonStart(bucket.start, gran),
          value: series.previous[i],
        })
      })
      index.registerSource(
        `${CUSTOM_KEY_PREFIX}${metric.id}`,
        metric.name,
        points,
      )
    }
    return index
  }, [
    revenueKPIBaseData,
    brandValueBreakdownRawData,
    mediaCampaignsRawData,
    adsetInitiativeData,
    granularity,
    amazonEnabled,
    availabilityDailyRows,
    kpiWindows,
    comparisonStartOf,
    customMetrics,
    customSeriesById,
    columnBuckets,
  ])

  const processMetricsData = useMemo(
    () => (metrics: MetricDefinition[]) =>
      processMetrics(
        revenueKPIData,
        "metrics.date",
        granularity,
        withDictLabels(metrics),
        availabilityIndex,
      ),
    [revenueKPIData, granularity, availabilityIndex, withDictLabels],
  )

  const processBrandValueData = useMemo(
    () => (metrics: MetricDefinition[]) =>
      processMetrics(
        brandValueBreakdownRawData,
        "company_platform_brand_value.engagement_date",
        granularity,
        withDictLabels(metrics),
        availabilityIndex,
        { columnBuckets, rowByBucketStart: calcSecondaryRows.brand_value },
      ),
    [
      brandValueBreakdownRawData,
      granularity,
      availabilityIndex,
      withDictLabels,
      columnBuckets,
      calcSecondaryRows,
    ],
  )

  // Display rows for typed custom metrics, each carrying its primary table's
  // anchor keys plus the TBR-row fallback (see foldTargetsForMetric). The
  // VALUES were already folded into the aggregate columns above — these rows
  // only make the composition visible: buildCustomGroupData inserts them into
  // custom-report groups, and index.tsx places each into exactly one builtin
  // table via insertTypedRowsAcrossTables (it knows render order/eligibility).
  const typedRowInsertions = useMemo<TypedRowInsertion[]>(() => {
    const insertions: TypedRowInsertion[] = []
    for (const metric of customMetrics) {
      const targets = foldTargetsForMetric(metric)
      if (!targets || targets.insertAnchors.length === 0) continue
      const [row] = processCustomMetrics(
        columnBuckets,
        resolveCatalogMetrics(
          [`${CUSTOM_KEY_PREFIX}${metric.id}`],
          [],
          customEntries,
        ) as MetricDefinition[],
        customSeriesById,
      )
      if (row) {
        insertions.push({
          anchorKeys: targets.insertAnchors,
          fallbackAnchorKeys: targets.fallbackInsertAnchors,
          row,
        })
      }
    }
    return insertions
  }, [customMetrics, columnBuckets, customEntries, customSeriesById])

  // Row keys a custom group's sum-all-rows totals footer must skip: a typed
  // custom metric's own row (inserted or explicitly listed) when the group
  // also shows an aggregate row its values were folded into — otherwise the
  // footer counts the value twice (once inside the folded aggregate, once via
  // the metric's row). Builtin tables are immune (explicit marginalMetrics).
  const typedMarginalExcludeKeys = useCallback(
    (rowKeys: ReadonlyArray<string | number | null | undefined>): string[] => {
      const keySet = new Set(rowKeys)
      const exclude: string[] = []
      for (const metric of customMetrics) {
        const rowKey = `${CUSTOM_KEY_PREFIX}${metric.id}`
        if (!keySet.has(rowKey)) continue
        const targets = foldTargetsForMetric(metric)
        if (!targets) continue
        if (targets.mainColumns.some(column => keySet.has(column))) {
          exclude.push(rowKey)
        }
      }
      return exclude
    },
    [customMetrics],
  )

  // Builds rows for a config-driven custom group from catalog metric keys. The
  // keys can mix cube sources (the main `metrics.*` query and the separate
  // `company_platform_brand_value.*` query), so each subset is processed against
  // its own dataset and the results are re-interleaved into the configured key
  // order. Labels/types come from the catalog (never from stored config).
  const buildCustomGroupData = useMemo(
    () =>
      (
        metricKeys: string[],
        calcDefs: CalculatedMetricDef[] = [],
      ): Array<Record<string, string | number | null | undefined>> => {
        const metrics = withDictLabels(
          resolveCatalogMetrics(metricKeys, calcDefs, catalogExtraEntries),
        )
        const mainMetrics = metrics.filter(
          metric => sourceForMetricKey(metric.key) === "metrics",
        )
        const brandValueMetrics = metrics.filter(
          metric => sourceForMetricKey(metric.key) === "brand_value",
        )
        const mediaCampaignsMetrics = metrics.filter(
          metric => sourceForMetricKey(metric.key) === "media_campaigns",
        )
        const initiativeMetrics = metrics.filter(
          metric => sourceForMetricKey(metric.key) === "initiatives",
        )
        const computedMetrics = metrics.filter(
          metric => sourceForMetricKey(metric.key) === "computed",
        )
        const calculatedMetrics = metrics.filter(
          metric => sourceForMetricKey(metric.key) === "calculated",
        )
        const customMetricDefs = metrics.filter(
          metric => sourceForMetricKey(metric.key) === "custom",
        )
        const mainRows = processMetrics(
          revenueKPIData,
          "metrics.date",
          granularity,
          mainMetrics as MetricDefinition[],
          customGroupAvailabilityIndex,
        )
        const brandValueRows = processMetrics(
          brandValueBreakdownRawData,
          "company_platform_brand_value.engagement_date",
          granularity,
          brandValueMetrics as MetricDefinition[],
          customGroupAvailabilityIndex,
          { columnBuckets, rowByBucketStart: calcSecondaryRows.brand_value },
        )
        const mediaCampaignsRows = processMetrics(
          mediaCampaignsRawData,
          "company_media_metrics_campaigns.md_date",
          granularity,
          mediaCampaignsMetrics as MetricDefinition[],
          customGroupAvailabilityIndex,
          {
            columnBuckets,
            rowByBucketStart: calcSecondaryRows.media_campaigns,
          },
        )
        // The initiative dataset is already pivoted to one row per date with
        // initiative.* / initiative_category.* column keys, so the shared row
        // builder consumes it exactly like a cube dataset.
        const initiativeRows = processMetrics(
          foldedAdsetInitiativeData,
          "company_media_metrics_adsets.md_date",
          granularity,
          initiativeMetrics as MetricDefinition[],
          undefined,
          { columnBuckets, rowByBucketStart: calcSecondaryRows.initiatives },
        )
        // Computed metrics derive per period from the main `metrics.*` row plus
        // the resolved Amazon cost settings.
        const computedRows = processComputedMetrics(
          revenueKPIData,
          granularity,
          amazonFinancials,
          computedMetrics as MetricDefinition[],
          customGroupAvailabilityIndex,
        )
        // User-defined calculated metrics evaluate their stored formula per
        // period over the main `metrics.*` row, with computed/custom leaves,
        // sibling-cube leaves (joined by bucket date), and nested calc refs.
        const calcDefsByKey = new Map(
          calcDefs.map(def => [`${CALC_KEY_PREFIX}${def.id}`, def]),
        )
        const calculatedRows = processCalculatedMetrics(
          revenueKPIData,
          resolveDateKey(revenueKPIData, "metrics.date", granularity),
          amazonFinancials,
          calculatedMetrics as MetricDefinition[],
          calcDefsByKey,
          customSeriesById,
          calcSecondaryRows,
          customGroupAvailabilityIndex,
        )
        // User-entered custom metrics bucket their stored values onto the columns.
        const customRows = processCustomMetrics(
          columnBuckets,
          customMetricDefs as MetricDefinition[],
          customSeriesById,
        )
        const rowByKey = new Map<string, KpiRow>()
        for (const row of [
          ...mainRows,
          ...brandValueRows,
          ...mediaCampaignsRows,
          ...initiativeRows,
          ...computedRows,
          ...calculatedRows,
          ...customRows,
        ]) {
          rowByKey.set(row.key as string, row)
        }
        // Preserve the configured metric order; drop any keys that produced no row.
        const orderedRows = metrics
          .map(metric => rowByKey.get(metric.key))
          .filter((row): row is KpiRow => row !== undefined)
        // Typed custom metrics render beside their target row here too, so a
        // custom report containing e.g. Total Ad Spend agrees with the builtin
        // dashboard. A group that already lists the metric keeps its one copy.
        return applyTypedRowInsertions(
          orderedRows,
          typedRowInsertions,
          new Set(metricKeys),
        )
      },
    [
      revenueKPIData,
      brandValueBreakdownRawData,
      mediaCampaignsRawData,
      foldedAdsetInitiativeData,
      amazonFinancials,
      granularity,
      catalogExtraEntries,
      customSeriesById,
      calcSecondaryRows,
      columnBuckets,
      customGroupAvailabilityIndex,
      withDictLabels,
      typedRowInsertions,
    ],
  )

  // Process all metric types
  const processedData = useMemo(() => {
    const revenueKpiConfig = VISUALIZATIONS_MAP.revenue_kpi_dashboard_table
    const metricGroups = revenueKpiConfig?.props?.metricGroups || {}

    // Generate dynamic revenue breakdown metrics from charts configuration
    const revenueBreakdownConfig = metricGroups.revenueBreakdown
    const revenueBreakdownMetrics = revenueBreakdownConfig?.getMetrics
      ? revenueBreakdownConfig.getMetrics(depVar, amazonEnabled)
      : []

    // Generate dynamic total business revenue metrics
    const totalBusinessRevenueConfig = metricGroups.totalBusinessRevenue
    const totalBusinessRevenueMetrics = totalBusinessRevenueConfig?.getMetrics
      ? totalBusinessRevenueConfig.getMetrics(
          amazonEnabled,
          isShopifyConnected,
          isWholesaleConnected,
        )
      : totalBusinessRevenueConfig?.metrics || []

    // Generate dynamic GA revenue metrics (always using multi-property)
    const gaRevenueConfig = metricGroups.gaRevenue
    const gaRevenueMetrics = gaRevenueConfig?.getMetrics
      ? gaRevenueConfig.getMetrics()
      : gaRevenueConfig?.metrics || []

    // NOTE: typed custom-metric display rows are NOT inserted here — index.tsx
    // places each into exactly one rendered table (insertTypedRowsAcrossTables
    // over `typedRowInsertions`), because only the render layer knows table
    // order and availability. The aggregate VALUES are already folded into
    // these rows at the dataset level (applyTypedColumnDeltas above).
    return {
      adSpendData: processMetricsData(metricGroups.adSpend?.metrics || []),
      merData: processMetricsData(metricGroups.mer?.metrics || []),
      croWebMerchData: processMetricsData(
        metricGroups.croWebMerch?.metrics || [],
      ),
      searchKPIsData: processMetricsData(
        metricGroups.searchKPIs?.metrics || [],
      ),
      metaAdsReachImpressionsData: processMetricsData(
        metricGroups.metaAdsReachImpressions?.metrics || [],
      ),
      metaAdsAudienceSegmentsData: processMetricsData(
        metricGroups.metaAdsAudienceSegments?.metrics || [],
      ),
      trafficData: processMetricsData(metricGroups.traffic?.metrics || []),
      averageOrderValuesData: processMetricsData(
        metricGroups.averageOrderValues?.metrics || [],
      ),
      totalBusinessRevenueData: processMetricsData(totalBusinessRevenueMetrics),
      brandData: processMetricsData(metricGroups.brand?.metrics || []),
      gaRevenueData: processMetricsData(gaRevenueMetrics),
      shopifyRevenueData: processMetricsData(
        metricGroups.shopifyRevenue?.metrics || [],
      ),
      shopifyCustomerData: processMetricsData(
        metricGroups.shopifyCustomer?.metrics || [],
      ),
      shopifyEcommRevenueData: processMetricsData(
        metricGroups.shopifyEcommRevenue?.metrics || [],
      ),
      shopifyRetailRevenueData: processMetricsData(
        metricGroups.shopifyRetailRevenue?.metrics || [],
      ),
      amazonRevenueData: processMetricsData(
        metricGroups.amazonRevenue?.metrics || [],
      ),
      // Only the retailers the company has a connected feed for (+ the total).
      wholesaleData: processMetricsData(
        filterWholesaleMetrics(
          metricGroups.wholesale?.metrics || [],
          connectedWholesaleServices,
        ),
      ),
      shopifyContributionData: processMetricsData(
        metricGroups.shopifyContribution?.metrics || [],
      ),
      brandValueData: processMetricsData(
        metricGroups.brandValue?.metrics || [],
      ),
      digitalEngagementData: processMetricsData(
        metricGroups.digitalEngagement?.metrics || [],
      ),
      revenueBreakdownData: processMetricsData(revenueBreakdownMetrics),
      brandValueBreakdownData: processBrandValueData(
        metricGroups.brandValueBreakdown?.metrics || [],
      ),
    }
  }, [
    processMetricsData,
    processBrandValueData,
    depVar,
    amazonEnabled,
    baselineRevenueMetric,
    isGaMultiPropertyEnabled,
    connectedWholesaleServices,
    isWholesaleConnected,
  ])

  // Create column configuration dynamically based on data
  const columnConfig = useMemo(() => {
    const dateKey = resolveDateKey(revenueKPIData, "metrics.date", granularity)

    if (!revenueKPIData || revenueKPIData.length === 0) {
      return [{ key: "metric", header: "Metric", type: "title" }]
    }

    const sortedData = [...revenueKPIData].sort(
      (a, b) =>
        new Date(a[dateKey] as string).getTime() -
        new Date(b[dateKey] as string).getTime(),
    )

    const columns: Array<{
      key: string
      header: string
      type: string
      note?: string
    }> = [{ key: "metric", header: "Metric", type: "title" }]
    const gran = GRANULARITY_SUFFIX[granularity] as BucketGranularity

    // Add a column for each week/month
    sortedData.forEach((weekData, index) => {
      // Use the actual date from the data
      const actualDate = dayjs(weekData[dateKey] as string)
      const dateFormatMap = {
        [AggregationType.DAILY]: "MMM DD YYYY",
        [AggregationType.WEEKLY]: "MMM DD YYYY",
        [AggregationType.MONTHLY]: "MMM YYYY",
      }
      const dateFormat = dateFormatMap[granularity]
      const weekDate = actualDate.format(dateFormat)
      const weekKey = `week_${index + 1}`

      // The newest weekly/monthly bucket is usually still in progress while
      // its YoY twin is a full prior-year period; the header says so.
      const bucketStart = String(weekData[dateKey] ?? "").slice(0, 10)
      const note =
        index === sortedData.length - 1 && bucketStart.length === 10
          ? partialBucketNote(
              bucketStart,
              resolveComparisonDate(
                weekData,
                dateKey,
                bucketStart,
                gran,
                comparisonStartOf,
              ),
              gran,
            )
          : null

      columns.push({
        key: weekKey,
        header: weekDate,
        type: "date",
        ...(note ? { note } : {}),
      })
    })

    return columns
  }, [revenueKPIData, dayjs, granularity, comparisonStartOf])

  return {
    revenueKPIData,
    // The resolved fetch windows (current/comparison/availability ranges +
    // compare mode) — the Explorer labels its axes and pickers from these.
    windows: kpiWindows,
    // Period bucket per column ([start, end] + week_N key), index-aligned
    // with columnConfig; also available via customEditMeta.columnBuckets.
    columnBuckets,
    isLoading,
    brandValueIsLoading,
    mediaCampaignsIsLoading,
    initiativesIsLoading,
    financialsLoading,
    customMetricsLoading,
    customEditMeta,
    processedData,
    buildCustomGroupData,
    columnConfig,
    customEntries,
    initiativeEntries,
    catalogExtraEntries,
    typedRowInsertions,
    typedMarginalExcludeKeys,
    depVar,
    amazonEnabled,
    toggleAmazon,
    isGaMultiPropertyEnabled,
  }
}
